<template>
  <div
    name="groups"
    class="groups"
  >
    <div
      v-for="(group, groupIndex) in groupList"
      :key="groupIndex"
      ref="group-body"
      class="groups__group-body"
      :class="{
        'groups__group-body--active': activeGroupIndex === groupIndex,
      }"
    >
      <button
        class="groups__group-body__head"
        @click="toggleGroup(groupIndex)"
      >
        {{ group.label }}
        <IconArrow
          :direction="activeGroupIndex === groupIndex ? 'up' : 'down'"
        />
      </button>
      <Tabs
        v-if="group.tabs && group.tabs.length > 0"
        :tab-list="groups.tabs"
      />
      <div
        v-else
        class="groups__group-body__content"
      >
        <p
          v-if="group.helpText"
          class="help-text"
        >
          <IconHelp
            size="xs"
            color="#3e9aa6"
          />
          <span
            class="mb-3 text-muted"
            v-html="group.helpText"
          />
        </p>
        <component
          v-bind="field"
          :is="UiTypes[field.type]"
          v-for="field in group.fields"
          :key="`${field.name}-${activeEditableGroup}`"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import * as UiTypes from '@/ui-types';
import IconArrow from './icons/IconArrow.vue';
import IconHelp from './icons/IconHelp.vue';
import IconCopy from './icons/IconCopy.vue';
import Tabs from './Tabs.vue';

export default {
  name: 'Groups',
  components: {
    Tabs,
    IconArrow,
    IconHelp,
    IconCopy,
  },
  inheritAttrs: false,
  props: {
    tabIndex: {
      type: Number,
      default: 0,
    },
    groupList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      UiTypes,
      activeGroupIndex: -1,
    };
  },
  computed: {
    activeEditableGroup() {
      return this.activeGlobalData ?? this.activeInstance;
    },
    ...mapState([
      'activeGlobalData',
      'activeInstance',
      'activeInstanceExpandedGroup',
    ]),
  },
  watch: {
    activeInstance: {
      handler() {
        if (this.activeInstanceExpandedGroup !== null) {
          const splited = this.activeInstanceExpandedGroup.split('-');
          this.activeGroupIndex = Number(splited[1]);
        } else {
          this.activeGroupIndex = -1;
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleGroup(groupIndex) {
      if (this.activeGroupIndex === groupIndex) {
        this.activeGroupIndex = -1;
        this.setActiveInstanceExpandedGroup(null);
      } else {
        this.activeGroupIndex = groupIndex;
        this.setActiveInstanceExpandedGroup(`${this.tabIndex}-${groupIndex}`);
      }
    },
    ...mapActions(['setActiveInstanceExpandedGroup']),
  },
};
</script>
<style scoped lang="scss">
.groups {
  &__group-body {
    margin-top: 8px;

    &__head {
      color: $crm-blue-primary;
      border: 1px solid #edf8f9;
      padding: 12px 20px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: #edf8f9;
      border-radius: 4px;
      font-size: $size-normal;
      font-weight: $lb-light;
      cursor: pointer;

      &:not(:first-child) {
        margin-top: 8px;
      }

      &.group-head--active {
        border-radius: 4px 4px 0 0;
      }
    }

    &__content {
      display: flex;
      flex-wrap: wrap;
      width: 100%;
      height: 0;
      overflow: hidden;

      > p,
      > div {
        width: 100%;
      }

      .help-text {
        margin-left: 20px;
        color: $lb-gray;
        font-size: $size-smaller;
        font-weight: $lb-light;
        margin-top: 1em;
        margin-bottom: 1em;

        .icon {
          margin-right: 8px;
        }
      }

      .field {
        margin-left: 20px;
        margin-right: 20px;

        &:first-child {
          margin-top: 15px;
        }

        &:last-child {
          margin-bottom: 15px;
        }

        &--disabled {
          opacity: 0.35;
          cursor: not-allowed;

          &::v-deep > * {
            pointer-events: none;
          }
        }
      }
    }

    &--active {
      .groups {
        &__group-body {
          &__head {
            border-radius: 4px 4px 0 0;
          }

          &__content {
            height: auto;
            border: 1px solid $gray-200;
            border-top: none;
            border-radius: 0 0 4px 4px;
            overflow: initial;
          }
        }
      }
    }
  }
}
</style>
